/* eslint-disable no-shadow */
import classNames from 'classnames';
import {SearchPanel} from 'modules/search-popup/components/SearchPanel';
import {SearchPanelBackdrop} from 'modules/search-popup/components/SearchPanelBackdrop';
import React, {
    FormEvent,
    InputHTMLAttributes,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import style from './style.module.scss';
import {MAX_SEARCH_TERM_LENGTH} from 'modules/search-popup/constants';
import {useSearchModule} from 'modules/search-popup/hooks/useSearchModule';
import {IDataAutotestProps} from 'core/types';
import Icon from 'components/Icon';
import {getSearchSuggestions, useGetSearchSuggestionsQuery} from 'modules/search-popup/services';
import {useSearchInputPlaceholder} from 'modules/search-popup/hooks/useSearchInputPlaceholder';
import {useRouter} from 'next/router';
import _debounce from 'lodash/debounce';
import {ISearchLink} from 'modules/search-popup/models';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {checkIsNextPath} from 'core/helpers/href-checker';
import {DEBOUNCE_DELAY_SECOND} from 'core/constants';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';
import {ButtonNew} from 'components/ButtonNew';
import {disableScroll, enableScroll} from 'core/helpers';

export interface IProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string;
    isNeedClose?: boolean;
    // isNeedClearSearch?: boolean;
    isHeader?: boolean;
    onClose?: () => void;
    isPanelVisible?: boolean;
    setIsPanelVisible?: (visibility: boolean) => void;
    hasClearField?: boolean;
}

// eslint-disable-next-line complexity,max-lines-per-function
export const Search = memo<IProps & IDataAutotestProps>(function Search({
    isPanelVisible,
    setIsPanelVisible,
    className,
    isNeedClose = false,
    // isNeedClearSearch = false,
    isHeader = false,
    hasClearField = true,
    onClose,
    dataAutotestTarget,
    ...restInputAttributes
}) {
    const dispatch = useAppDispatch();
    const router = useRouter();

    const searchQuery = router.query.q;

    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    const normalizedSearchQuery =
        // eslint-disable-next-line no-nested-ternary
        'string' === typeof searchQuery ? searchQuery : Array.isArray(searchQuery) ? (searchQuery[0] ?? '') : '';

    const queryForSuggestions = debouncedSearchValue || normalizedSearchQuery || '';

    const {
        data,
        isLoading: isSearchDataLoading,
        isFetching: isSearchDataFetching,
    } = useGetSearchSuggestionsQuery(queryForSuggestions ? {queryStr: queryForSuggestions} : undefined);

    const placeholderList = data?.placeholderList;

    const {
        // clearSearch,
        onChange,
        handleSearchClick,
        handlePanelClose,
        handleCrossClick,
        handleSubmit,
    } = useSearchModule(searchValue, setSearchValue, setIsPanelVisible, onClose);

    const currentPlaceholder = useSearchInputPlaceholder('', placeholderList);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const uid = useId();

    const suggestionsData = useMemo(() => [...(data?.suggestions ?? []), ...(data?.link ?? [])], [data]);

    const recommendedProducts = useMemo(() => data?.products, [data]);
    const errors = useMemo(() => [], []);

    const renderSearchLoader = (isLoaderVisible: boolean) => {
        if (!isLoaderVisible) {
            return null;
        }
        return (
            <div className={style.searchLoaderIcon}>
                <Icon icon={'search-loader'} />
            </div>
        );
    };

    const renderSearchClearAction = (
        renderSearchLoader: ReactNode,
        hasClearField: boolean,
        searchValue: string,
        handleCrossClick: () => void,
    ) => {
        if (renderSearchLoader) {
            return renderSearchLoader;
        }

        if (!hasClearField) {
            return null;
        }

        return (
            <button
                className={classNames(style.labelCross, {
                    [style.labelCrossVisible]: 0 < searchValue.length,
                })}
                onClick={handleCrossClick}
                type={'reset'}
                aria-label={'Стереть'}
            >
                <Icon icon={'search-cancel'} />
            </button>
        );
    };

    const handleFormSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            const getSingleUrl = (links: ISearchLink[] | undefined): string | undefined => {
                if (!links || !Array.isArray(links) || 0 === links.length) {
                    return undefined;
                }
                return links[0]?.url;
            };

            const redirectToUrl = (url: string) => {
                if (checkIsNextPath(url)) {
                    router.push(url);
                    return;
                }
                window.location.href = url;
            };

            if (searchValue) {
                try {
                    const data = await dispatch(getSearchSuggestions.initiate({queryStr: searchValue})).unwrap();
                    const url = getSingleUrl(data?.link);
                    if (url) {
                        redirectToUrl(url.includes('/catalog/product') ? withRedirectParam(url) : url);
                        return;
                    }
                } catch (_) {}
            }
            await handleSubmit(currentPlaceholder)();
        },
        [searchValue, handleSubmit, currentPlaceholder, router, dispatch],
    );

    useEffect(() => {
        if ('' === searchValue) {
            setDebouncedSearchValue('');
            return;
        }
        const handler = _debounce((val) => setDebouncedSearchValue(val), DEBOUNCE_DELAY_SECOND);
        handler(searchValue);
        // eslint-disable-next-line consistent-return
        return () => handler.cancel();
    }, [searchValue]);

    useEffect(() => {
        if (isPanelVisible) {
            disableScroll();
        } else {
            enableScroll();
        }

        return () => {
            enableScroll();
        };
    }, [isPanelVisible]);

    useEffect(() => {
        if (searchQuery && searchQuery !== searchValue) {
            setSearchValue(searchQuery as string);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    return (
        <>
            <form
                className={classNames(style.form, className, {
                    [style.header]: isHeader,
                })}
                onSubmit={handleFormSubmit}
            >
                <label
                    className={classNames(style.label, {
                        [style.labelBackdropVisible]: isPanelVisible,
                    })}
                    htmlFor={uid}
                >
                    {/* {isNeedClearSearch && <button className={style.clearButton} onClick={clearSearch} type="reset" />}*/}
                    <input
                        {...restInputAttributes}
                        className={classNames(style.input, 'header-search-form__input', {
                            // [style.withClearSearch]: isNeedClearSearch,
                            [style.inputOpenModal]: isPanelVisible,
                        })}
                        id={uid}
                        maxLength={MAX_SEARCH_TERM_LENGTH}
                        onChange={onChange}
                        onClick={handleSearchClick}
                        placeholder={currentPlaceholder}
                        ref={searchInputRef}
                        value={searchValue}
                        type={'search'}
                        name={'search'}
                        autoComplete={'off'}
                        autoCorrect={'off'}
                        autoCapitalize={'off'}
                        spellCheck={'false'}
                    />
                    <div className={style.searchButtonWrapper}>
                        <ButtonNew
                            className={classNames(style.searchButton, 'header-search-form__submit')}
                            variant={'small'}
                            theme={'black'}
                            type={'submit'}
                            aria-label={'Найти'}
                        >
                            Найти
                        </ButtonNew>
                    </div>
                    {renderSearchClearAction(
                        renderSearchLoader(!isSearchDataLoading && isSearchDataFetching),
                        hasClearField,
                        searchValue,
                        handleCrossClick,
                    )}
                </label>
                <SearchPanel
                    errors={errors}
                    isLoading={isSearchDataFetching}
                    isVisible={Boolean(isPanelVisible)}
                    recommendedProducts={recommendedProducts}
                    searchValue={searchValue}
                    suggestions={suggestionsData}
                />
                {isNeedClose && isPanelVisible && (
                    <ButtonNew
                        className={style.closeButton}
                        onClick={handlePanelClose}
                        type={'button'}
                        theme={'white'}
                        variants={{MB: 'medium', TB: 'large', DT: 'large'}}
                    >
                        {/* <Typography element="div" variant="body-16-reg">*/}
                        Закрыть
                        {/* </Typography>*/}
                    </ButtonNew>
                )}
            </form>
            {!isHeader && <SearchPanelBackdrop isVisible={Boolean(isPanelVisible)} onClose={handlePanelClose} />}
        </>
    );
});
